import { CustomSelectComponent } from './../../Core/custom-select/custom-select.component';
import { Component, EventEmitter, inject, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../../Services/auth.service';
import { NotificationService } from '../../../Services/notification.service';
import { initFlowbite } from 'flowbite';
import { SendOtpComponent } from "../send-otp/send-otp.component";
import { NgIf } from '@angular/common';
import { NavbarService } from '../../../Services/navbar.service';
import { FirmSelectionService } from '../../../Services/firm-selection.service';
import { jwtDecode } from 'jwt-decode';

@Component({
  selector: 'app-login',
  imports: [CustomSelectComponent, RouterLink, ReactiveFormsModule, SendOtpComponent, NgIf],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit {

  loginForm!: FormGroup;
  errorMessage: string = '';
  showPassword: boolean = false;
  selectedTab = 'login';
  generatedCaptcha: string = '';
  isGstLoading: boolean = false;
  isCaptchaLoading: boolean = false;
  isLoading: boolean = false;
  userCaptcha: string = '';
  UserType: string[] = [
    'Staff',
    'Owner'
  ];
  selectedUser: string = '';

  authService = inject(AuthService);

  constructor(
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private router: Router,
    private navbarService: NavbarService,
    private firmSelectionService: FirmSelectionService
  ) { }

  ngOnInit(): void {
    this.generateCaptcha();
    this.inItForm();
    this.watchCaptchaChanges(); // 👈 add this line
    initFlowbite()
    this.handleUserTypeChange(); // add this
  }

  inItForm() {
    this.loginForm = this.fb.group({
      owner_email: [''],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required,]],//Validators.minLength(6),
      Captcha: [this.generatedCaptcha, [Validators.required]],
      user_type: ['', [Validators.required]],
    });
  }
  watchCaptchaChanges() {
    const captchaControl = this.loginForm.get('Captcha');

    captchaControl?.valueChanges.subscribe(value => {
      if (!value) {
        captchaControl.setErrors({ required: true });
        return;
      }

      if (value !== this.generatedCaptcha) {
        captchaControl.setErrors({ invalidCaptcha: true });
      } else {
        captchaControl.setErrors(null);
      }
    });
  }
  handleUserTypeChange() {
    this.loginForm.get('user_type')?.valueChanges.subscribe(value => {
      const ownerEmailControl = this.loginForm.get('owner_email');
      const emailControl = this.loginForm.get('email');
      const passwordControl = this.loginForm.get('password');

      if (value === 'Staff') {
        // Owner email is required only for Staff
        ownerEmailControl?.setValidators([Validators.required, Validators.email]);
        ownerEmailControl?.setValue(''); // reset previous owner email

        // Staff email is required for Staff
        emailControl?.setValidators([Validators.required, Validators.email]);
        emailControl?.setValue(''); // reset previous email
      } else {
        // Clear Owner email validators for Owner
        ownerEmailControl?.clearValidators();
        ownerEmailControl?.setValue(''); // reset owner email

        // Email is required for Owner as well
        emailControl?.setValidators([Validators.required, Validators.email]);
        emailControl?.setValue(''); // reset previous email
      }

      // Reset password whenever user_type changes
      passwordControl?.setValue('');

      // Re-validate fields
      ownerEmailControl?.updateValueAndValidity();
      emailControl?.updateValueAndValidity();
      passwordControl?.updateValueAndValidity();
    });
  }



  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  onSubmit() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    const enteredCaptcha = this.loginForm.get('Captcha')?.value;

    if (enteredCaptcha !== this.generatedCaptcha) {
      this.notificationService.showError('Invalid Captcha. Please try again.', 'Error');
      this.generateCaptcha(); // regenerate captcha
      this.loginForm.get('Captcha')?.reset(); // clear input
      return;
    }

    let payload = { ...this.loginForm.value };

    if (payload.user_type === 'Owner') {
      delete payload.owner_email;
    } else if (payload.user_type === 'Staff') {
      if (!payload.owner_email) {
        this.notificationService.showError('Owner email is required for staff login', 'Error');
        return;
      }
    }

    this.isLoading = true;

    this.authService.login(payload).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response.success) {
          this.errorMessage = '';
          this.notificationService.showSuccess(response.message, 'Success');
          // ✅ SESSION-BASED STORAGE
          sessionStorage.setItem('token', response.token);

          const decoded: any = jwtDecode(response.token);
          sessionStorage.setItem('role', decoded.role ?? '');

          const userRole = decoded.role;
          const userSubRole = decoded.sub_role;

          this.navbarService.updateSettingsVisibility();

          if (response.firm_id) {
            localStorage.setItem('firm_id', response.firm_id);
            sessionStorage.setItem('is_firm_restricted', 'true');

            // Set context immediately to trigger app.component redirection
            if (response.firm_name && response.gst_status) {
              this.firmSelectionService.setselectedFirmName({
                id: response.firm_id,
                name: response.firm_name,
                status: response.gst_status
              });
            }
          } else {
            sessionStorage.removeItem('is_firm_restricted');
          }

          if (userRole === 'Owner') {
            const isGst = response.gst_status && this.firmSelectionService.isGstFirm(response.gst_status, response.firm_name);
            if (isGst) {
              this.router.navigate(['/invoice-gst-panel']);
            } else {
              this.router.navigate(['/home']);
            }
          }
          else if (userRole === 'Staff') {
            if (userSubRole === 'Karigar') {
              localStorage.removeItem('firm_id');
              sessionStorage.removeItem('is_firm_restricted');
              this.router.navigate(['/kalakaar-management']);
            }
            else if (userSubRole === 'Chartered Accountant') {
              this.router.navigate(['/invoice-gst-panel']);
            }
            else if (userSubRole === 'Customer') {
              this.router.navigate(['/customer-management']);
            }
            else {
              // For other staff, navigate based on GST status
              const isGst = response.gst_status && this.firmSelectionService.isGstFirm(response.gst_status, response.firm_name);
              if (isGst) {
                this.router.navigate(['/invoice-gst-panel']);
              } else {
                this.router.navigate(['/rm-portal/home']);
              }
            }
          }

        } else {
          this.errorMessage = response.message;
        }
      },

      error: (error: any) => {
        this.isLoading = false;
        let backendMessage = 'Login failed. Please try again';

        // 1️⃣ Prefer field-level Laravel validation errors
        if (error?.error?.errors) {
          const allErrors = Object.values(error.error.errors)
            .flat()
            .filter((msg: any) => typeof msg === 'string');

          if (allErrors.length > 0) {
            backendMessage = allErrors[0];
          }
        }
        // 2️⃣ If no validation errors, fallback to message
        else if (error?.error?.message) {
          backendMessage = error.error.message;
        }

        this.errorMessage = backendMessage;
        this.notificationService.showError(this.errorMessage, 'Error');
      }
    });
  }

  selectTab(tab: string): void {
    this.selectedTab = tab;
  }

  homePage() {
    this.router.navigate(['/home']);
  }
  refreshCaptcha() {
    this.isCaptchaLoading = true;

    setTimeout(() => {
      this.generateCaptcha();
      this.isCaptchaLoading = false;
    }, 1000);
  }

  generateCaptcha() {
    const captchaChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    this.generatedCaptcha = '';
    for (let i = 0; i < 6; i++) {
      this.generatedCaptcha += captchaChars.charAt(Math.floor(Math.random() * captchaChars.length));
    }
  }
}
